<template>
    <Teleport to="body" :disabled="!isFullscreen">
        <div ref="tileWorkspace" :class="['mmm-uploader', 'tile-upload-step', {'tile-upload-step--fullscreen': isFullscreen}]" :role="isFullscreen ? 'dialog' : undefined" :aria-modal="isFullscreen ? 'true' : undefined" :aria-label="isFullscreen ? $t('design_step_2') : undefined">
            <div class="tile-upload-toolbar">
                <div class="tile-upload-count" aria-live="polite">
                    <span class="tile-upload-count-number">{{ $store.getters.tile_count }}</span>
                    <div><strong>{{ $t('design_tiles_uploaded') }}</strong><span>{{ $t('design_tiles_recommendation') }}</span></div>
                </div>
                <button ref="fullscreenButton" type="button" class="btn-uploader btn-uploader-dark tile-upload-fullscreen-button" :aria-pressed="isFullscreen" v-on:click="toggleFullscreen">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path v-if="!isFullscreen" d="M8 3H3v5m13-5h5v5M3 16v5h5m13-5v5h-5"/><path v-else d="M3 8h5V3m8 0v5h5M8 21v-5H3m13 5v-5h5"/></svg>
                    {{ $t(isFullscreen ? 'exit_fullscreen' : 'design_expand') }}
                </button>
            </div>
            <div id="tiles-upload" class="tile-upload-dashboard"/>
            <div v-if="errors.length" class="alert-uploader" role="alert">
                <b>{{ $t('fix_errors') }}</b>
                <ul class="upload-error-list">
                    <li v-for="error in errors" :key="error.id" class="upload-error">
                        <span><strong v-if="error.fileName">{{ error.fileName }}:</strong> {{ error.message }}</span>
                        <span class="upload-error-actions">
                            <button v-if="canRemoveErrorFile(error)" type="button" class="upload-error-action" v-on:click="removeErrorFile(error)">{{ $t('remove_image') }}</button>
                            <button type="button" class="upload-error-action" v-on:click="dismissUploadError(error.id)">{{ $t('dismiss_warning') }}</button>
                        </span>
                    </li>
                </ul>
            </div>
            <div class="tile-upload-tools">
                <p>{{ $t('design_tiles_hint') }}</p>
                <div class="tile-upload-tool-buttons">
                    <button type="button" :disabled="!$store.getters.tile_count || isUploadBusy()" v-on:click="openTileEditor" class="btn-uploader btn-uploader-dark">{{ $t('rotate_images') }}</button>
                    <button type="button" :disabled="!$store.getters.tile_count || isUploadBusy()" v-on:click="deleteAllFiles" class="btn-uploader btn-uploader-danger">{{ $t('remove_all_images') }}</button>
                </div>
            </div>
            <div class="uploader-actions">
                <button type="button" class="btn-uploader btn-uploader-dark" v-on:click="$store.dispatch('DECREMENT')"><span aria-hidden="true">←</span>{{ $t('back') }}</button>
                <button type="button" class="btn-uploader" v-on:click="checkForm()">{{ $t('design_next_product') }}<span aria-hidden="true">→</span></button>
            </div>
            <dialog ref="tileEditor" class="tile-editor" aria-labelledby="tile-editor-title" @close="showTileEditor = false">
                <div class="tile-editor-header">
                    <h3 id="tile-editor-title">{{ $t('rotate_images') }}</h3>
                    <button type="button" class="tile-editor-close" :aria-label="$t('close')" @click="closeTileEditor">×</button>
                </div>
                <p v-if="tileEditorError" class="alert-uploader" role="alert">{{ tileEditorError }}</p>
                <div class="tile-editor-grid">
                    <div v-for="tile in $store.getters.tile_images" :key="tile.name" class="tile-editor-card">
                        <img :src="tile.url" :alt="tile.original_name || tile.name" class="tile-editor-image">
                        <p :title="tile.original_name || tile.name">{{ tile.original_name || tile.name }}</p>
                        <div class="tile-editor-controls">
                            <button type="button" :disabled="isEditingTile" :aria-label="$t('design_rotate_left', {filename: tile.original_name || tile.name})" v-on:click="rotateTile(tile.name, -90)">↶</button>
                            <button type="button" :disabled="isEditingTile" :aria-label="$t('design_rotate_right', {filename: tile.original_name || tile.name})" v-on:click="rotateTile(tile.name, 90)">↷</button>
                            <button type="button" :disabled="isEditingTile" class="tile-editor-delete" :aria-label="$t('design_remove_named', {filename: tile.original_name || tile.name})" v-on:click="deleteTileEditor(tile.name)">×</button>
                        </div>
                    </div>
                </div>
                <div class="tile-editor-footer"><button type="button" class="btn-uploader" @click="closeTileEditor">{{ $t('close') }}</button></div>
            </dialog>
        </div>
    </Teleport>
</template>

<script>
    import PreviewService from "@/services/preview.service";
    import TileService from "@/services/tile.service";
    import * as types from "@/store/mutation-types";
    import * as Constants from "../constants";
    import axios from 'axios';
    import {TileUppy, createTileBatchPreparer} from "@/services/tile-upload.service";
    import XHRUpload from "@uppy/xhr-upload";
    import Dashboard from "@uppy/dashboard";
    import {markRaw} from "vue";
    import {Language} from "@/i18n";
    import UploadLog from "@/services/upload-log.service";
    import {limitDashboardFocusToKeyboard} from "@/services/dashboard-focus.service";
    import {
        describeFile,
        getImageValidationErrorKey,
        getUppyAllowedFileTypes,
        IMAGE_UPLOAD_RESTRICTIONS,
        validateImageFile,
        validateImageMetadata
    } from "@/services/image-validation.service";

    // Uppy fires per-file events; with hundreds of files those are collected and
    // written to the upload log as one summary line per event type.
    const LOG_BATCH_DELAY = 1500;
    const LOG_BATCH_EXAMPLES = 5;

    export default {
        name: "StepTwo",
        data: function() {
            return {
                errors: [],
                uppy: null,
                isValid: true,
                showTileEditor: false,
                isEditingTile: false,
                tileEditorError: null,
                isDeletingAll: false,
                validationQueue: Promise.resolve(),
                pendingValidations: 0,
                uploadStartedAt: null,
                logBatches: {},
                logBatchTimer: null,
                isFullscreen: false,
                previousBodyOverflow: null,
                duplicateFileCounter: 0,
                skippedDuplicateIds: {},
                uploadErrorCounter: 0,
            }
        },
        methods: {
            logBatched: function(event, entry, reason) {
                const batch = this.logBatches[event] || (this.logBatches[event] = { count: 0, reasons: {}, examples: [] });
                batch.count++;
                if(reason) {
                    batch.reasons[reason] = (batch.reasons[reason] || 0) + 1;
                }
                if(batch.examples.length < LOG_BATCH_EXAMPLES) {
                    batch.examples.push(entry);
                }

                clearTimeout(this.logBatchTimer);
                this.logBatchTimer = setTimeout(() => this.flushLogBatches(), LOG_BATCH_DELAY);
            },
            flushLogBatches: function() {
                clearTimeout(this.logBatchTimer);
                const batches = this.logBatches;
                this.logBatches = {};

                Object.keys(batches).forEach(event => {
                    UploadLog.log(event, batches[event]);
                });
            },
            uploadSnapshot: function() {
                const files = this.uppy ? this.uppy.getFiles() : [];

                return {
                    files: files.length,
                    uploaded: files.filter(file => file.progress && file.progress.uploadComplete).length,
                    in_progress: files.filter(file => file.progress && file.progress.uploadStarted && !file.progress.uploadComplete).length,
                    with_error: files.filter(file => file.error).length,
                    tile_count: this.$store.getters.tile_count,
                    upload_running: this.isUploadBusy()
                };
            },
            isUploadBusy: function() {
                return this.isDeletingAll || this.isEditingTile || this.pendingValidations > 0 ||
                    (this.uppy && (Object.keys(this.uppy.getState().currentUploads).length > 0 ||
                        this.uppy.getFiles().some(file => file.source !== 'Remote' &&
                            !file.progress.uploadComplete && !file.error)));
            },
            onPageHidden: function() {
                if(this.isUploadBusy()) {
                    this.flushLogBatches();
                    UploadLog.log('page_hidden_during_upload', this.uploadSnapshot());
                }
            },
            onVisibilityChange: function() {
                if(document.visibilityState === 'hidden') {
                    this.onPageHidden();
                }
            },
            showUploadError: function(messageKey, file = null) {
                const message = this.$t(messageKey);
                const fileName = file && (file.meta && file.meta.original_name || file.name);
                const fileId = file && file.id;
                this.showUploadMessage(message, `${messageKey}:${fileId || fileName || 'general'}`, file);
            },
            showUploadMessage: function(message, identity, file = null) {
                const fileName = file && (file.meta && file.meta.original_name || file.name);
                const fileId = file && file.id;
                if(!this.errors.some(error => error.identity === identity)) {
                    this.uploadErrorCounter = (this.uploadErrorCounter || 0) + 1;
                    this.errors = [...this.errors, {
                        id: `upload-error-${this.uploadErrorCounter}`,
                        identity,
                        message,
                        fileId,
                        fileName
                    }];
                }
            },
            dismissUploadError: function(errorId) {
                this.errors = this.errors.filter(error => error.id !== errorId);
            },
            canRemoveErrorFile: function(error) {
                return Boolean(error.fileId && this.uppy && this.uppy.getFile(error.fileId));
            },
            removeErrorFile: function(error) {
                if(this.canRemoveErrorFile(error)) {
                    this.uppy.removeFile(error.fileId);
                }
                this.dismissUploadError(error.id);
            },
            createDuplicateFile: function(file, files, decision) {
                if(decision !== 'Keep') {
                    return false;
                }

                const fileIds = new Set(Array.isArray(files) ? files.map(item => item.id) : Object.keys(files));
                let id;
                do {
                    this.duplicateFileCounter = (this.duplicateFileCounter || 0) + 1;
                    id = `${file.id}-duplicate-${this.duplicateFileCounter}`;
                } while(fileIds.has(id));

                return {
                    ...file,
                    id,
                    meta: {
                        ...file.meta,
                        original_id: file.meta.original_id || file.id,
                        original_name: file.meta.original_name || file.name
                    }
                };
            },
            isDuplicateFile: function(file, files) {
                const originalName = String(file.meta.original_name || file.name || '').toLowerCase();

                return Object.values(files).some(existingFile => {
                    const sameOriginalId = (existingFile.meta.original_id || existingFile.id) === file.meta.original_id;
                    const sameLegacyFile = existingFile.meta.legacy_original_name &&
                        existingFile.meta.legacy_original_name.toLowerCase() === originalName &&
                        existingFile.type === file.type;

                    return sameOriginalId || sameLegacyFile;
                });
            },
            markSkippedDuplicate: function(fileId) {
                this.skippedDuplicateIds[fileId] = (this.skippedDuplicateIds[fileId] || 0) + 1;
            },
            consumeSkippedDuplicate: function(fileId) {
                const count = this.skippedDuplicateIds[fileId] || 0;
                if(count === 0) {
                    return false;
                }
                if(count === 1) {
                    delete this.skippedDuplicateIds[fileId];
                } else {
                    this.skippedDuplicateIds[fileId] = count - 1;
                }

                return true;
            },
            toggleFullscreen: function() {
                this.isFullscreen = !this.isFullscreen;
                if(this.isFullscreen) {
                    this.previousBodyOverflow = document.body.style.overflow;
                    document.body.style.overflow = 'hidden';
                } else {
                    document.body.style.overflow = this.previousBodyOverflow == null ? '' : this.previousBodyOverflow;
                    this.previousBodyOverflow = null;
                }
                if(this.$refs && this.$refs.fullscreenButton) {
                    this.$nextTick(() => this.$refs.fullscreenButton?.focus());
                }
            },
            onFullscreenKeydown: function(event) {
                if(event.key === 'Escape' && this.isFullscreen && !this.showTileEditor) {
                    this.toggleFullscreen();
                }
                if(event.key === 'Tab' && this.isFullscreen && !this.showTileEditor) {
                    const controls = [...this.$refs.tileWorkspace.querySelectorAll('button:not(:disabled), input:not(:disabled), [tabindex="0"]')]
                        .filter(element => element.getClientRects().length > 0);
                    const first = controls[0];
                    const last = controls[controls.length - 1];
                    if(event.shiftKey && document.activeElement === first) {
                        event.preventDefault();
                        last.focus();
                    } else if(!event.shiftKey && document.activeElement === last) {
                        event.preventDefault();
                        first.focus();
                    }
                }
            },
            validateAndUpload: function(files) {
                const localFiles = files.filter(file => file.source !== 'Remote' && !file.progress.uploadComplete);
                if(localFiles.length === 0) {
                    return this.validationQueue;
                }

                this.pendingValidations++;
                const typeCounts = {};
                localFiles.forEach(file => {
                    const key = `${(file.extension || '').toLowerCase()}|${file.type || ''}`;
                    typeCounts[key] = (typeCounts[key] || 0) + 1;
                });
                UploadLog.log('tiles_selected', {
                    count: localFiles.length,
                    total_size: localFiles.reduce((sum, file) => sum + (file.size || 0), 0),
                    types: typeCounts,
                    already_in_list: this.uppy.getFiles().length - localFiles.length
                });

                this.validationQueue = this.validationQueue.then(async () => {
                    const pendingFiles = this.uppy.getFiles().filter(file =>
                        file.source !== 'Remote' && !file.progress.uploadComplete && !file.error);
                    if(pendingFiles.length === 0) {
                        return;
                    }
                    UploadLog.log('tiles_upload_started', { queued: pendingFiles.length });
                    await this.uppy.upload();
                }).catch(error => {
                    UploadLog.logError('tiles_upload_failed', error);
                    this.showUploadError('upload_failed');
                }).finally(() => {
                    this.pendingValidations--;
                });

                return this.validationQueue;
            },
            translateChoice: function(key, count, values = {}) {
                const translated = this.$t(key, {
                    count,
                    ...values
                });
                const choices = translated.split('|').map(choice => choice.trim());

                return count === 1 ? choices[0] : choices[choices.length - 1];
            },
            deleteAllFiles: async function() {
                if(this.isUploadBusy()) {
                    this.showUploadError('wait_for_upload');
                    return;
                }
                if(confirm(this.translateChoice('delete_tiles_confirmation', this.$store.getters.tile_images.length))) {
                    UploadLog.log('tiles_delete_all', { tile_count: this.$store.getters.tile_images.length });
                    this.isDeletingAll = true;
                    this.errors = [];

                    const files = this.uppy.getFiles();
                    const tiles = [...this.$store.getters.tile_images];
                    const results = await Promise.allSettled(tiles.map(tile => {
                        return TileService.delete(this.$store.getters.preview, tile.name)
                            .then(response => response.data.name);
                    }));
                    const deletedNames = results
                        .filter(result => result.status === 'fulfilled')
                        .map(result => result.value);

                    deletedNames.forEach(name => {
                        this.$store.dispatch(types.TILE_IMAGE_DELETED, name);
                    });

                    files.forEach(file => {
                        if(deletedNames.includes(file.name) && this.uppy.getFile(file.id)) {
                            this.uppy.removeFile(file.id);
                        }
                    });

                    if(results.some(result => result.status === 'rejected')) {
                        this.errors = [];
                        this.showUploadError('delete_tiles_failed');
                    }

                    this.isDeletingAll = false;
                }
            },
            checkForm: function() {
                if(this.isUploadBusy()) {
                    this.isValid = false;
                    this.errors = [];
                    this.showUploadError('wait_for_upload');
                    UploadLog.log('tiles_next_blocked', { reason: 'wait_for_upload', ...this.uploadSnapshot() });
                } else if(this.$store.getters.tile_count < 20) {
                    this.isValid = false;
                    this.errors = [];
                    this.showUploadMessage(
                        this.$t('min_20_tiles') + " " + this.translateChoice('not_enough_tiles', this.$store.getters.tile_count),
                        'min_20_tiles'
                    );
                    UploadLog.log('tiles_next_blocked', { reason: 'min_20_tiles', ...this.uploadSnapshot() });
                } else {
                    this.flushLogBatches();
                    this.$store.dispatch('INCREMENT')
                    this.errors = [];
                    this.isValid = true;
                }
            },
            openTileEditor: function() {
                this.tileEditorError = null;
                this.showTileEditor = true;
                this.$refs.tileEditor.showModal();
            },
            closeTileEditor: function() {
                this.showTileEditor = false;
                this.$refs.tileEditor.close();
            },
            rotateTile: async function(filename, direction) {
                if(this.isEditingTile) {
                    return;
                }
                this.isEditingTile = true;
                this.tileEditorError = null;
                try {
                  const vm = this;
                    const response = await TileService.rotate(
                        this.$store.getters.preview,
                        filename,
                        direction
                    )
                    this.$store.getters.tile_images.forEach(function(tile) {
                        if(tile.name === filename) {
                            tile.name = response.data.name;
                            tile.url = Constants.STORAGE_URL + vm.$store.getters.preview + '/tile/' + response.data.name + '?t=' + Date.now();
                        }
                    })
                    this.uppy.getFiles().forEach(file => {
                        if(file.name === filename) {
                            this.uppy.setFileState(file.id, {
                                name: response.data.name,
                                preview: Constants.STORAGE_URL + this.$store.getters.preview + '/tile/' + response.data.name + '?t=' + Date.now()
                            });
                        }
                    });
                } catch(error) {
                    this.tileEditorError = this.$t('design_rotation_failed');
                    UploadLog.logError('tile_rotate_failed', error);
                } finally {
                    this.isEditingTile = false;
                }
            },
            deleteTileEditor: function(filename) {
                if(confirm(this.$t('confirm_delete_single_tile'))) {
                    this.deleteTile(filename)
                }
            },
            deleteTile: async function(filename) {
                if(this.isEditingTile) {
                    return;
                }
                this.isEditingTile = true;
                this.tileEditorError = null;
                try {
                    const response = await TileService.delete(
                        this.$store.getters.preview,
                        filename
                    )
                    this.$store.dispatch(types.TILE_IMAGE_DELETED, response.data.name)
                    const file = this.uppy.getFiles().find(file => file.name === filename);
                    if(file) {
                        this.uppy.setFileState(file.id, {progress: {...file.progress, uploadComplete: false}});
                        this.uppy.removeFile(file.id);
                    }
                } catch(error) {
                    this.tileEditorError = this.$t('delete_tiles_failed');
                    UploadLog.logError('tile_delete_failed', error);
                } finally {
                    this.isEditingTile = false;
                }
            }
        },
        beforeUnmount() {
            document.removeEventListener('visibilitychange', this.onVisibilityChange);
            document.removeEventListener('keydown', this.onFullscreenKeydown);
            window.removeEventListener('pagehide', this.onPageHidden);
            if(this.isFullscreen) {
                this.toggleFullscreen();
            }
            this.flushLogBatches();
        },
        mounted() {
            if(this.$store.getters.preview == null) {
                PreviewService.create().then(response => {
                        this.$store.dispatch(types.NEW_PREVIEW, {
                            preview: response.data
                        });
                        this.uppy.setMeta({
                            code: this.$store.getters.preview
                        });
                    },
                    error => {
                        console.log((error.response && error.response.data) ||
                            error.message ||
                            error.toString());
                    }
                )
            }

            let prepareBatch;
            const batchStartedAt = new Map();
            const uppy = new TileUppy({
                debug: false,
                locale: Language.uppy,
                autoProceed: false,
                restrictions: {
                    maxFileSize: IMAGE_UPLOAD_RESTRICTIONS.maxFileSize,
                    maxNumberOfFiles: 3000,
                    minNumberOfFiles: 1,
                    allowedFileTypes: getUppyAllowedFileTypes()
                },
                onBeforeFileAdded: (file, files) => {
                    const errorKey = validateImageMetadata(file);
                    if(errorKey) {
                        this.showUploadError(errorKey, file);
                        this.logBatched('tile_rejected_client', describeFile(file), errorKey);
                        return false;
                    }

                    let preparedFile = {
                        ...file,
                        meta: {
                            ...file.meta,
                            original_id: file.meta.original_id || file.id,
                            original_name: file.meta.original_name || file.name
                        }
                    };
                    const duplicate = this.isDuplicateFile(preparedFile, files);
                    if(duplicate && file.source !== 'Remote') {
                        const keepDuplicate = confirm(this.$t('duplicate_upload_confirmation', {
                            filename: preparedFile.meta.original_name
                        }));
                        preparedFile = this.createDuplicateFile(preparedFile, files, keepDuplicate ? 'Keep' : 'Skip');
                        if(!keepDuplicate) {
                            this.markSkippedDuplicate(file.id);
                        }
                        UploadLog.log('tile_duplicate_decision', {
                            file: describeFile(file),
                            decision: keepDuplicate ? 'keep' : 'skip'
                        });
                    }

                    return preparedFile;
                }
            }, {
                prepareBatch: (...args) => prepareBatch(...args),
                onBatchStart: (fileIDs, index, total, uploadID) => {
                    batchStartedAt.set(`${uploadID}:${index}`, Date.now());
                },
                onBatchComplete: (fileIDs, index, total, uploadID) => {
                    const key = `${uploadID}:${index}`;
                    UploadLog.log('tile_batch_uploaded', {
                        upload_id: uploadID,
                        batch: index + 1,
                        batches: total,
                        count: fileIDs.length,
                        successful: fileIDs.filter(id => uppy.getFile(id)?.progress.uploadComplete).length,
                        transfer_ms: Date.now() - batchStartedAt.get(key)
                    });
                    batchStartedAt.delete(key);
                }
            });
            prepareBatch = createTileBatchPreparer(uppy, {
                compressionOptions: Constants.TILE_COMPRESSION_OPTIONS,
                validate: file => validateImageFile(file, (error, details) => {
                    this.logBatched('tile_file_read_error', {
                        file: describeFile(file),
                        name: error.name,
                        message: error.message,
                        ...details
                    }, error.name);
                }),
                onValidationError: (file, errorKey) => {
                    this.showUploadError(errorKey, file);
                    this.logBatched('tile_rejected_client', describeFile(file), errorKey);
                    uppy.setFileState(file.id, { error: this.$t(errorKey) });
                },
                onCompressionError: (file, error) => {
                    this.logBatched('tile_compression_failed', {
                        file: describeFile(file),
                        message: error.message
                    }, error.message);
                },
                onPrepared: (data, context) => UploadLog.log('tile_batch_prepared', {
                    ...data,
                    upload_id: context.uploadID,
                    batch: context.batch + 1,
                    batches: context.total
                })
            });
            uppy
                .use(Dashboard, {
                  trigger: '.UppyModalOpenerBtn',
                  inline: true,
                  target: '#tiles-upload',
                  replaceTargetContent: true,
                  showProgressDetails: true,
                  hideUploadButton: true,
                  disableInformer: true,
                  showRemoveButtonAfterComplete: true,
                  proudlyDisplayPoweredByUppy: false,
                  doneButtonHandler: null,
                  note: this.$t('design_file_types'),
                  height: 420,
                  width: '100%',
                  browserBackButtonClose: false,
                  locale: {
                    strings: Language.dashboard.tiles
                  }
                })
                .use(XHRUpload, {
                  endpoint: Constants.API_URL + 'v2/upload/tile',
                  formData: true,
                  fieldName: 'image',
                  onBeforeRequest: (xhr, retryCount, files) => {
                      if(retryCount > 0) {
                          files.forEach(file => this.logBatched('tile_upload_retry', {
                              file: describeFile(file),
                              attempt: retryCount + 1
                          }));
                      }
                  }
                });
            this.uppy = markRaw(uppy);
            limitDashboardFocusToKeyboard(this.uppy);
            this.uppy.setMeta({
                code: this.$store.getters.preview
            });

            this.uppy.on('files-added', files => {
                this.validateAndUpload(files);
            });

            this.uppy.on('restriction-failed', (file, error) => {
                if(file && this.consumeSkippedDuplicate(file.id)) {
                    return;
                }
                // Rejections from onBeforeFileAdded are already handled there.
                if(file && !(error && error.isUserFacing === false)) {
                    // Uppy's own checks (accept list, max size) fail silently since
                    // the informer is disabled, so surface them in the error box.
                    this.showUploadError(validateImageMetadata(file) || 'image_validation_file', file);
                    this.logBatched('tile_rejected_uppy', describeFile(file), error && error.message ? error.message : String(error));
                }
            });

            this.uppy.on('upload-error', (file, error, response) => {
                const errorKey = getImageValidationErrorKey(error, response);
                this.showUploadError(errorKey || 'upload_failed', file);
                this.logBatched('tile_upload_error', {
                    file: describeFile(file),
                    status: response ? response.status : null,
                    message: error && error.message ? error.message : String(error),
                    response: response && typeof response.responseText === 'string' ? response.responseText.slice(0, 300) : null
                }, response ? `http_${response.status}` : (error && error.message ? error.message : 'unknown'));
            });

            this.uppy.on('upload-success', (file, response) => {
                let found = false;

                this.uppy.setFileState(file.id, {
                    name: response.body.data.name
                });

                this.$store.getters.tile_images.forEach((tile) => {
                    if(response.body.data.name === tile.name) {
                        found = true;
                    }
                });

                if(!found) {
                    this.$store.dispatch(types.INCREMENT_TILECOUNT);
                    this.$store.dispatch(types.TILE_IMAGE_UPLOADED, {
                        original_name: file.meta.original_name || file.name,
                        original_id: file.meta.original_id || file.id,
                        type: file.type,
                        name: response.body.data.name,
                        url: Constants.STORAGE_URL + this.$store.getters.preview + '/tile/' + response.body.data.name
                    });
                }
            });

            this.uppy.on('upload', () => {
                if(!this.uploadStartedAt) {
                    this.uploadStartedAt = Date.now();
                }
            });

            this.uppy.on('complete', result => {
                if(!result || result.successful.length + result.failed.length === 0) {
                    if(Object.keys(this.uppy.getState().currentUploads).length === 0) {
                        this.uploadStartedAt = null;
                    }
                    return;
                }
                UploadLog.log('tiles_upload_complete', {
                    successful: result && result.successful ? result.successful.length : null,
                    failed: result && result.failed ? result.failed.length : null,
                    duration_ms: this.uploadStartedAt ? Date.now() - this.uploadStartedAt : null,
                    tile_count: this.$store.getters.tile_count
                });
                this.flushLogBatches();
                if(Object.keys(this.uppy.getState().currentUploads).length === 0) {
                    this.uploadStartedAt = null;
                }
            });

            this.uppy.on('upload-stalled', (error, files) => {
                files.forEach(file => this.logBatched('tile_upload_stalled', {
                    file: describeFile(file),
                    message: error.message
                }));
            });

            document.addEventListener('visibilitychange', this.onVisibilityChange);
            document.addEventListener('keydown', this.onFullscreenKeydown);
            window.addEventListener('pagehide', this.onPageHidden);

            this.uppy.on('file-removed', (file) => {
                this.errors = this.errors.filter(error => error.fileId !== file.id);
                if(this.isDeletingAll) {
                    return;
                }

                if(!file.progress.uploadComplete) {
                    return;
                }

                TileService.delete(
                    this.$store.getters.preview,
                    file.name
                ).then(response => {
                    this.$store.dispatch(types.TILE_IMAGE_DELETED, response.data.name)
                    return response.data;
                }).catch(error => {
                    console.log((error.response && error.response.data) ||
                        error.message ||
                        error.toString());
                    this.errors = [];
                    this.showUploadError('delete_tiles_failed');
                });
            });

            this.$nextTick(() => {
                this.$emit('scroll-to-app');
            });

            this.$nextTick(() => {
                //Code fügt bestehende Images nach einem reload wieder ein
                this.$store.getters.tile_images.forEach((file) => {
                    axios.get(file.url, {responseType: "blob"})
                         .then(response =>  {
                            const preview =  URL.createObjectURL(response.data);
                            const autoProceed = this.uppy.opts.autoProceed;
                            this.uppy.setOptions({autoProceed: false});

                            try {
                                const fileId = this.uppy.addFile({
                                    name: file.name,
                                    type: file.type,
                                    data: response.data,
                                    meta: {
                                        name: file.original_name,
                                        original_id: file.original_id,
                                        original_name: file.original_name,
                                        legacy_original_name: file.original_id ? null : file.original_name,
                                        relativePath: null,
                                        compressed: true,
                                    },
                                    source: 'Remote',
                                    isRemote: false,
                                    isPaused: false
                                });
                                this.uppy.setFileState(fileId, {
                                    progress: {
                                        uploadComplete: true,
                                        uploadStarted: true,
                                    }
                                });
                                this.uppy.setFileState(fileId, { preview });
                                if(this.uppy.getFiles().every(file => file.progress.uploadComplete)) {
                                    this.uppy.setState({totalProgress: 100});
                                }
                            } finally {
                                this.uppy.setOptions({autoProceed});
                            }
                        }).catch(error => {
                            this.logBatched('tile_restore_failed', {
                                name: file.name,
                                status: error.response ? error.response.status : null,
                                message: error.message
                            }, error.response ? `http_${error.response.status}` : 'network');
                        });
                })
            });
        }
    }
</script>

<style scoped>
    .tile-upload-step { display: flex; flex-direction: column; min-width: 0; }
    .tile-upload-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 16px; }
    .tile-upload-count { display: flex; align-items: center; gap: 12px; }
    .tile-upload-count-number { display: grid; place-items: center; min-width: 50px; height: 50px; padding: 0 10px; background: var(--uploader-green-tint); color: var(--uploader-green-dark); border-radius: 12px; font-size: 23px; font-weight: 700; }
    .tile-upload-count strong { display: block; font-size: 14px; }
    .tile-upload-count div > span { display: block; color: var(--uploader-muted); font-size: 12px; }
    .tile-upload-step .tile-upload-fullscreen-button { width: auto; flex-shrink: 0; font-size: 13px; padding: 10px 14px; }
    .tile-upload-dashboard { width: 100%; min-width: 0; }
    .tile-upload-tools { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-top: 16px; }
    .tile-upload-tools p { font-size: 12px; color: var(--uploader-muted); margin: 0; max-width: 320px; }
    .tile-upload-tool-buttons { display: flex; gap: 8px; flex-shrink: 0; }
    .tile-upload-tool-buttons .btn-uploader { min-height: 44px; width: auto; font-size: 12px; padding: 10px 12px; }
    .tile-upload-step--fullscreen { position: fixed; z-index: 100000; inset: 0; box-sizing: border-box; height: 100vh; height: 100dvh; padding: 24px; overflow-y: auto; background: #fff; }
    .tile-upload-step--fullscreen .tile-upload-dashboard { position: relative; flex: 1 0 240px; min-height: 240px; }
    .tile-upload-step--fullscreen :deep(.uppy-Root) { position: absolute; inset: 0; }
    .tile-upload-step--fullscreen :deep(.uppy-Dashboard) { height: 100%; }
    .tile-upload-step--fullscreen :deep(.uppy-Dashboard-inner) { height: 100% !important; max-height: none !important; }
    .upload-error { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px; margin-top: 12px; }
    .upload-error > span:first-child { flex: 1 1 240px; overflow-wrap: anywhere; }
    .upload-error-actions { display: flex; flex-wrap: wrap; gap: 8px; }
    .upload-error-action { min-height: 44px; padding: 8px 12px; border: 1px solid var(--uploader-orange-line); border-radius: 8px; background: var(--uploader-orange-tint); color: var(--uploader-orange-dark); cursor: pointer; font-size: 12px; }
    .tile-editor { padding: 0; width: min(1100px, calc(100vw - 40px)); max-width: none; max-height: 90vh; max-height: 90dvh; border: 1px solid var(--uploader-line); border-radius: 20px; color: var(--uploader-ink); background: white; box-shadow: 0 24px 80px #00000040; }
    .tile-editor::backdrop { background: #000000b3; }
    .tile-editor-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 24px; border-bottom: 1px solid var(--uploader-line); }
    .tile-editor-header h3 { font-size: 20px; margin: 0; color: var(--uploader-ink); }
    .tile-editor-close { width: 44px; height: 44px; font-size: 28px; border: 0; background: var(--uploader-green-tint); border-radius: 50%; cursor: pointer; color: var(--uploader-green-dark); }
    .tile-editor-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 16px; max-height: 60vh; overflow-y: auto; padding: 24px; }
    .tile-editor-card { min-width: 0; border: 1px solid var(--uploader-line); border-radius: 12px; overflow: hidden; }
    .tile-editor-image { display: block; width: 100%; height: 150px; object-fit: contain; background: var(--uploader-soft); }
    .tile-editor-card p { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12px; padding: 8px 10px 0; margin: 0; }
    .tile-editor-controls { display: flex; justify-content: center; padding: 4px; }
    .tile-editor-controls button { width: 44px; height: 44px; border: 0; background: transparent; color: var(--uploader-green-dark); font-size: 26px; cursor: pointer; }
    .tile-editor-controls .tile-editor-delete { color: #a34438; }
    .tile-editor-footer { padding: 16px 24px; border-top: 1px solid var(--uploader-line); text-align: right; }
    .tile-editor-footer button { width: auto; min-width: 140px; }
    @media (max-width: 767px) {
        .tile-upload-toolbar { align-items: flex-start; gap: 8px; }
        .tile-upload-count { gap: 8px; }
        .tile-upload-count-number { min-width: 42px; height: 44px; font-size: 20px; }
        .tile-upload-count strong { font-size: 12px; }
        .tile-upload-count div > span { max-width: 145px; font-size: 11px; }
        .tile-upload-step .tile-upload-fullscreen-button { font-size: 12px; min-height: 44px; padding: 8px 10px; gap: 6px; max-width: 140px; }
        .tile-upload-tools { flex-direction: column; align-items: stretch; gap: 12px; }
        .tile-upload-tool-buttons .btn-uploader { flex: 1; }
        .tile-upload-step--fullscreen { padding: 16px; }
        .tile-editor { width: calc(100vw - 16px); border-radius: 14px; }
        .tile-editor-header, .tile-editor-footer { padding: 12px 16px; }
        .tile-editor-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; padding: 16px; }
    }
</style>
